import React from 'react';
import { BiInfoCircle } from 'react-icons/bi';
import { useSelector } from 'react-redux';

import { Modal, Tag, Tooltip } from 'antd';
import { useFetchGUnits } from 'hooks/useFetchGUnits';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { IUnit } from 'interfaces/IInput';
import {
  type IInputStep,
  InputStepType,
  SeedDressRateMode,
} from 'interfaces/IInputStep';
import { formatCurrency } from 'utils/CurrencyUtils';

import { selectActiveGOrgId } from '../../../../redux/selectors/GlobalCatalogueSelectors';

interface InputStepDetailsModalProps {
  step: IInputStep;
  onClose: () => void;
}

// Some catalogue-sourced units (e.g. an active constituent's concentration
// unit) only carry a globalId, with `unit` left blank — fall back to the
// org's unit catalogue to resolve a display code.
const resolveUnitCode = (
  unit: IUnit | null | undefined,
  gUnits: GUnit[]
): string | null => {
  if (unit?.unit) return unit.unit;
  if (!unit?.globalId) return null;
  return gUnits.find((u) => u.globalId === unit.globalId)?.code ?? null;
};

const formatUnitValue = (
  value: number | null | undefined,
  unit?: string | null
): string | null => {
  if (value === null || value === undefined) return null;
  return unit ? `${value} ${unit}` : `${value}`;
};

const formatCostValue = (value: number | null | undefined): string | null =>
  value === null || value === undefined ? null : formatCurrency(value);

// No space, matching the ag-chemical label convention (e.g. "400g/L").
const formatConcentrationValue = (
  value: number | null | undefined,
  unit?: string | null
): string | null => {
  if (value === null || value === undefined) return null;
  return unit ? `${value}${unit}` : `${value}`;
};

const TAG_CLASS_NAME = 'h-fit rounded px-1 py-0.5 text-xs leading-4';

// A plain function (not a component) so a hidden row resolves to `null` in
// DetailSection's own children array, rather than to a non-null <DetailRow />
// element that happens to render null internally — the latter can't be
// detected by DetailSection's emptiness check.
const detailRow = (label: string, value: React.ReactNode): React.ReactNode => {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div key={label} className="flex flex-col gap-0.5">
      <span className="mb-1 block text-sm font-semibold text-gray-dark">
        {label}
      </span>
      <span className="text-base text-charcoal">{value}</span>
    </div>
  );
};

const DetailSection: React.FC<{
  title: string;
  // Rendered instead of hiding the section when it would otherwise have no
  // content (e.g. Nutrients, which should stay visible for Soil Input even
  // when every value is 0/unset).
  emptyState?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, emptyState, children }) => {
  const hasContent = React.Children.toArray(children).length > 0;
  if (!hasContent && !emptyState) return null;
  return (
    <div className="border-t border-gray-200 pt-3 first:border-t-0 first:pt-0">
      <h4 className="mb-2 text-base font-semibold text-charcoal">{title}</h4>
      {hasContent ? (
        <div className="grid grid-cols-2 gap-x-6 gap-y-3">{children}</div>
      ) : (
        emptyState
      )}
    </div>
  );
};

// Only a positive value is worth showing — a 0 or unset nutrient renders
// nothing, so the row list can be shorter than the full 16-nutrient set.
const nutrientRow = (label: string, value: number | null): React.ReactNode =>
  value ? detailRow(label, formatUnitValue(value, '%')) : null;

const InputStepDetailsModal: React.FC<InputStepDetailsModalProps> = ({
  step,
  onClose,
}) => {
  const gOrgId = useSelector(selectActiveGOrgId);
  // Full org unit catalogue — an active constituent's concentration unit
  // often only carries a globalId, so resolving its code needs every
  // parameter category, not just the Area-scoped list used elsewhere in
  // this workspace. Already warmed by OrganisationWorkspace, so this reads
  // from cache rather than issuing a new request.
  const { data: gUnits = [] } = useFetchGUnits(gOrgId);

  const isSoilInput = step.stepType === InputStepType.SoilInput;
  const isBiostimulant = step.stepType === InputStepType.Biostimulant;
  const isProduction = step.stepType === InputStepType.Production;
  const isSeed = step.stepType === InputStepType.Seed;

  const hasManufacturerSupplier = 'snapManufacturer' in step;
  const hasPlantProtection = 'withholdPeriods' in step;
  const hasProductGroups = 'productGroups' in step;

  const moaGroups = hasPlantProtection
    ? Array.from(
        new Set(
          (step.activeConstituents ?? [])
            .map((constituent) => constituent.snapMoaGroup)
            .filter((group): group is string => Boolean(group))
        )
      )
    : [];

  return (
    <Modal
      open
      centered
      title={step.item}
      onCancel={onClose}
      footer={null}
      width={640}
    >
      <div className="-mx-6 border-t border-gray-200" />
      <div className="mt-3 flex max-h-[calc(100vh-8rem)] flex-col gap-3 overflow-y-auto pr-1">
        {hasManufacturerSupplier && (
          <DetailSection title="Manufacturer & Supplier">
            {detailRow('Manufacturer', step.snapManufacturer)}
            {detailRow('Supplier', step.snapSupplier)}
          </DetailSection>
        )}

        {'rate' in step && (
          <DetailSection title="Rate & Cost">
            {detailRow(
              'Rate',
              formatUnitValue(
                step.rate,
                'appUnit' in step ? step.appUnit?.unit : null
              )
            )}
            {detailRow('Cost per unit', formatCostValue(step.cost))}
            {step.stepType === InputStepType.SeedDress &&
              detailRow(
                'Rate mode',
                step.rateMode === SeedDressRateMode.PerHa
                  ? 'Per hectare'
                  : 'Per 100kg'
              )}
          </DetailSection>
        )}

        {isSoilInput && (
          <DetailSection title="Product Details">
            {detailRow('Tech stack', step.snapTechStack)}
            {detailRow('Additive', step.additive)}
            {detailRow('Liquid', step.isLiquid ? 'Yes' : null)}
            {detailRow('Seed treatment', step.isSeedDressing ? 'Yes' : null)}
            {detailRow(
              'Bulk density',
              formatUnitValue(step.bulkDensity, step.bulkDensityUnit?.unit)
            )}
          </DetailSection>
        )}

        {isSoilInput && (
          <DetailSection
            title="Nutrients"
            emptyState={
              <div className="col-span-2 flex items-center gap-1.5 text-sm text-gray-muted">
                <span>No nutrient composition recorded</span>
                <Tooltip title="Every nutrient percentage for this product is 0% or has not been recorded.">
                  <span className="inline-flex">
                    <BiInfoCircle className="text-secondary-dark" />
                  </span>
                </Tooltip>
              </div>
            }
          >
            {nutrientRow('Nitrogen (N)', step.nutrientN)}
            {nutrientRow('Phosphorus (P)', step.nutrientP)}
            {nutrientRow('Potassium (K)', step.nutrientK)}
            {nutrientRow('Sulphur (S)', step.nutrientS)}
            {nutrientRow('Calcium (Ca)', step.nutrientCa)}
            {nutrientRow('Magnesium (Mg)', step.nutrientMg)}
            {nutrientRow('Sodium (Na)', step.nutrientNa)}
            {nutrientRow('Boron (B)', step.nutrientB)}
            {nutrientRow('Copper (Cu)', step.nutrientCu)}
            {nutrientRow('Iron (Fe)', step.nutrientFe)}
            {nutrientRow('Manganese (Mn)', step.nutrientMn)}
            {nutrientRow('Zinc (Zn)', step.nutrientZn)}
            {nutrientRow('Molybdenum (Mo)', step.nutrientMo)}
            {nutrientRow('Chlorine (Cl)', step.nutrientCl)}
            {nutrientRow('Silicon (Si)', step.nutrientSi)}
            {nutrientRow('Nickel (Ni)', step.nutrientNi)}
            {nutrientRow('Cobalt (Co)', step.nutrientCo)}
          </DetailSection>
        )}

        {isBiostimulant && (
          <DetailSection title="Product Details">
            {detailRow('Action mode', step.snapActionMode)}
          </DetailSection>
        )}

        {hasPlantProtection && (
          <>
            <DetailSection title="Product Details">
              {detailRow('Formulation', step.formulation)}
              {detailRow('Formulation code', step.formulationCode)}
              {detailRow('APVMA number', step.apvmaNumber)}
              {detailRow('Batch number', step.batchNumber)}
              {detailRow(
                'Poison schedule',
                step.poisonSchedule ? `Schedule ${step.poisonSchedule}` : null
              )}
              {detailRow(
                'Moa group',
                moaGroups.length > 0 ? moaGroups.join(', ') : null
              )}
              {detailRow(
                'Product groups',
                step.productGroups.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {step.productGroups.map((group, index) => (
                      <Tag
                        key={`${group.productGroupId ?? group.snapProductGroup}-${index}`}
                        className={TAG_CLASS_NAME}
                        color="var(--color-dark-gray)"
                      >
                        {group.snapProductGroup}
                      </Tag>
                    ))}
                  </div>
                ) : null
              )}
            </DetailSection>

            <DetailSection title="Active Constituents">
              {step.activeConstituents &&
                step.activeConstituents.length > 0 && (
                  <div className="col-span-2 overflow-x-auto">
                    <table className="w-full border-collapse text-sm">
                      <thead>
                        <tr>
                          <th className="border-b border-gray-200 px-2 py-1.5 text-left text-xs font-semibold text-gray-dark">
                            Name
                          </th>
                          <th className="border-b border-gray-200 px-2 py-1.5 text-left text-xs font-semibold text-gray-dark">
                            Concentration
                          </th>
                          <th className="border-b border-gray-200 px-2 py-1.5 text-left text-xs font-semibold text-gray-dark">
                            Group
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {step.activeConstituents.map((constituent, index) => (
                          <tr key={`${constituent.name}-${index}`}>
                            <td className="border-b border-gray-100 px-2 py-1.5 text-charcoal">
                              {constituent.name}
                            </td>
                            <td className="border-b border-gray-100 px-2 py-1.5 text-charcoal">
                              {formatConcentrationValue(
                                constituent.concentration,
                                resolveUnitCode(
                                  constituent.concentrationUnit,
                                  gUnits
                                )
                              ) ?? '-'}
                            </td>
                            <td className="border-b border-gray-100 px-2 py-1.5 text-charcoal">
                              {constituent.snapMoaGroup ?? '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
            </DetailSection>

            <DetailSection title="Withholding & Re-entry">
              {detailRow(
                'Re-entry period',
                formatUnitValue(step.reentry, step.reentryUnit?.unit)
              )}
              {detailRow('Re-entry notes', step.reentryNotes)}
              {step.withholdPeriods && step.withholdPeriods.length > 0 && (
                <div className="col-span-2 flex flex-col gap-1">
                  {step.withholdPeriods.map((withholdPeriod, index) => (
                    <div
                      key={`${withholdPeriod.globalId ?? withholdPeriod.item}-${index}`}
                      className="flex justify-between text-sm"
                    >
                      <span className="text-gray-dark">
                        {withholdPeriod.item}
                      </span>
                      <span className="text-charcoal">
                        {withholdPeriod.period !== null
                          ? `${withholdPeriod.period} days`
                          : '-'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </DetailSection>
          </>
        )}

        {hasProductGroups &&
          !hasPlantProtection &&
          step.productGroups.length > 0 && (
            <DetailSection title="Product Groups">
              <div className="col-span-2 flex flex-wrap gap-2">
                {step.productGroups.map((group, index) => (
                  <Tag
                    key={`${group.productGroupId ?? group.snapProductGroup}-${index}`}
                    className={TAG_CLASS_NAME}
                    color="var(--color-dark-gray)"
                  >
                    {group.snapProductGroup}
                  </Tag>
                ))}
              </div>
            </DetailSection>
          )}

        {isSeed && (
          <DetailSection title="Seed Details">
            {detailRow('Variety', step.item2)}
            {detailRow('Tech stack', step.snapTechStack)}
          </DetailSection>
        )}

        {isProduction && (
          <DetailSection title="Production">
            {detailRow('Target grade', step.targetGrade)}
            {detailRow(
              'Yield (low)',
              formatUnitValue(step.yieldLow, step.yieldUnit?.unit)
            )}
            {detailRow(
              'Yield (expected)',
              formatUnitValue(step.yield, step.yieldUnit?.unit)
            )}
            {detailRow(
              'Yield (high)',
              formatUnitValue(step.yieldHigh, step.yieldUnit?.unit)
            )}
            {detailRow(
              'Retain',
              formatUnitValue(step.retain, step.yieldUnit?.unit)
            )}
            {detailRow('Price (low)', formatCostValue(step.priceLow))}
            {detailRow('Price (expected)', formatCostValue(step.price))}
            {detailRow('Price (high)', formatCostValue(step.priceHigh))}
          </DetailSection>
        )}
      </div>
    </Modal>
  );
};

export default InputStepDetailsModal;
